import { forwardRef, type InputHTMLAttributes } from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/cn'

export interface NumberStepperProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'type' | 'value'> {
  /** Empty string means "unset" - parent maps to undefined / null. */
  value: string
  onChange: (next: string) => void
  min?: number
  max?: number
  step?: number
}

export const NumberStepper = forwardRef<HTMLInputElement, NumberStepperProps>(
  function NumberStepper(
    { value, onChange, min, max, step = 1, disabled, className, ...rest },
    ref,
  ) {
    const parsed = value === '' ? null : Number(value)
    const cur = parsed == null || Number.isNaN(parsed) ? null : parsed
    const clamp = (n: number) => {
      if (typeof min === 'number' && n < min) return min
      if (typeof max === 'number' && n > max) return max
      return n
    }
    const bump = (delta: number) => {
      const base = cur ?? (typeof min === 'number' ? min : 0)
      onChange(String(clamp(base + delta)))
    }
    const upDisabled = !!disabled || (cur != null && typeof max === 'number' && cur >= max)
    const downDisabled = !!disabled || (cur != null && typeof min === 'number' && cur <= min)

    return (
      <div className={cn('relative', className)}>
        <Input
          ref={ref}
          type="number"
          value={value}
          onChange={(e) => onChange(e.currentTarget.value)}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          className="pr-9"
          {...rest}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex w-8 flex-col overflow-hidden rounded-r-sm">
          <button
            type="button"
            tabIndex={-1}
            disabled={upDisabled}
            onClick={() => bump(step)}
            className="pointer-events-auto flex flex-1 items-center justify-center border-l border-input text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Increase"
          >
            <ChevronUp className="size-3.5" />
          </button>
          <button
            type="button"
            tabIndex={-1}
            disabled={downDisabled}
            onClick={() => bump(-step)}
            className="pointer-events-auto flex flex-1 items-center justify-center border-l border-t border-input text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Decrease"
          >
            <ChevronDown className="size-3.5" />
          </button>
        </div>
      </div>
    )
  },
)
